<script setup lang="ts">
import { SancareOcticon } from '@sancare/typescript-commons'
import { FilterDataValue, FilterEvent } from '@sancare/typescript-commons/src/types/filters'
import _ from 'lodash'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { useIsAuthorized } from '@/misc/composables/useIsAuthorized'
import { RoleEnum } from '@/misc/permissions/permissionsHelper'
import { documentCategories, textualHealthDataCategories } from '@/stay-displayer/health-data/documentLabels'
import { FilterData } from '@/store/modules/filters/types'

export interface TextContentCriteriaProps {
  filterData: FilterData
  initialValue?: FilterDataValue | null
  isGlobalCondition: boolean
  isLegacy: boolean
}

const props = withDefaults(
  defineProps<TextContentCriteriaProps>(),
  {
    initialValue: null,
    isGlobalCondition: false,
    isLegacy: true
  }
)

const emit = defineEmits<{
  (e: 'add-filter', filter: FilterEvent): void
}>()

const store = useStore()
const { t } = useI18n()

const presence = ref(props.isGlobalCondition ? 'softAbsent' : 'present')
const extendedSearch = ref(false)
const includeAntecedents = ref(false)
const includeFamilyAntecedents = ref(false)
const includeNegations = ref(false)
const hardAbsentContent = ref(false)
const specifyWordDistance = ref(false)
const specifyDocumentCategory = ref(false)
const value = ref('')
const wordDistance = ref(0)
const documentCategory = ref<string | null>(null)
const textualReportCategories = { ...documentCategories, ...textualHealthDataCategories }

const isSearchManager = useIsAuthorized([RoleEnum.ROLE_SANCARE_SEARCH_MANAGER], store.state.login.currentUser.role)

const filterOptions = computed(() => {
  const subOpt = props.filterData.subFilters[0]
  const isGlobalOptions = subOpt.isGlobalOptions ?? []
  const notGlobalOptions = subOpt.notGlobalOptions ?? subOpt.options

  const allowedOptions = props.isGlobalCondition ? isGlobalOptions : notGlobalOptions
  const options = props.isLegacy
    ? subOpt.notGlobalOptions
    : subOpt.options.filter((opt) => allowedOptions.includes(opt))

  if (props.initialValue) {
    return options.filter((opt) => opt === presence.value)
  }

  return options
})

const canSubmit = computed(() => {
  if (!value.value.trim()) return false

  if (specifyDocumentCategory.value && !documentCategory.value) {
    return false
  }

  if (specifyWordDistance.value && wordDistance.value <= 0) {
    return false
  }

  return true
})

function initializeFromInitialValue() {
  if (!props.initialValue) return

  resetModifiers()

  if (props.initialValue?.subFilters?.presence) {
    presence.value = props.initialValue.subFilters.presence
    if (presence.value === 'absent') {
      hardAbsentContent.value = true
      extendedSearch.value = true
    }
    if (presence.value === 'softAbsent' && props.isLegacy) {
      presence.value = 'absent'
    }
  }

  const rawValue = props.initialValue.value ?? ''
  const [modifiersRaw, textRaw] = rawValue.includes('\x1F') ? rawValue.split('\x1F') : [null, rawValue]
  value.value = textRaw || ''

  if (props.initialValue.documentCategory) {
    specifyDocumentCategory.value = true
    documentCategory.value = props.initialValue.documentCategory
  }

  if (!modifiersRaw) return

  extendedSearch.value = true

  const modifiers = modifiersRaw.split('_')

  modifiers.forEach((modifier) => {
    if (modifier === 'a') includeAntecedents.value = true
    else if (modifier === 'f') includeFamilyAntecedents.value = true
    else if (modifier === 'n') includeNegations.value = true
    else if (modifier.startsWith('wd')) {
      specifyWordDistance.value = true
      wordDistance.value = Number(modifier.replace('wd', ''))
    }
  })
}

watch(presence, () => {
  if (!props.initialValue) {
    resetModifiers()
  }
}, { flush: 'sync' })

watch(hardAbsentContent, (value) => {
  if (!value && !props.initialValue) {
    resetModifiers()
  }
}, { flush: 'sync' })

watch(
  () => props.initialValue,
  () => initializeFromInitialValue(),
  { immediate: true }
)

function addFilter() {
  if (!canSubmit.value) return

  let filterValue = value.value.trim()

  hardAbsentContent.value = props.isLegacy
    ? hardAbsentContent.value
    : !props.isGlobalCondition
  const modifiers = []
  if (includeAntecedents.value && (presence.value === 'present' || hardAbsentContent.value)) {
    modifiers.push('a')
  }
  if (includeFamilyAntecedents.value && (presence.value === 'present' || hardAbsentContent.value)) {
    modifiers.push('f')
  }
  if (includeNegations.value && (presence.value === 'present' || hardAbsentContent.value)) {
    modifiers.push('n')
  }
  if (specifyWordDistance.value) {
    modifiers.push(`wd${wordDistance.value}`)
  }

  // We build filterValue. If there are modifiers, they are put before a "Unit separator" (0x1F) character
  if (modifiers.length && (presence.value === 'present' || hardAbsentContent.value)) {
    filterValue = `${modifiers.join('_')}\x1F${value.value}`
  }

  const prsnc = presence.value === 'absent' && !hardAbsentContent.value ? 'softAbsent' : presence.value
  let type = `${prsnc}Content`
  if (specifyDocumentCategory.value) {
    type += `__${documentCategory.value}`
  }

  emit('add-filter', {
    name: props.filterData.name,
    type: type,
    value: filterValue,
  })
}

function resetModifiers() {
  includeAntecedents.value = false
  includeFamilyAntecedents.value = false
  includeNegations.value = false
  hardAbsentContent.value = !props.isLegacy && !props.isGlobalCondition
  specifyDocumentCategory.value = false
  wordDistance.value = 0
  documentCategory.value = null
}
</script>

<template>
  <div
    class="row"
    :class="isLegacy ? [] : ['px-3']"
  >
    <v-dropdown
      v-if="isLegacy"
      :triggers="['hover']"
      placement="top"
      popover-class="criteria-popover"
      class="col-auto"
    >
      <span class="saved-search-icon">
        <sancare-octicon
          name="question"
          :width="16"
          :height="30"
        />
      </span>
      <slot />
      <template #popper>
        <div style="width: 30rem;">
          <div>{{ t('filters.textCondition.presentContent') }}</div>
          <hr>
          <div>
            <div>{{ t('filters.textCondition.softAbsentContent') }}</div>
            <div class="ms-2">
              <small>{{ t('filters.textCondition.softAbsentContentTip') }}</small>
            </div>
          </div>
        </div>
      </template>
    </v-dropdown>
    <div
      v-if="isLegacy"
      class="col-10"
    >
      <div
        class="input-group"
      >
        <select
          id="text-content-criteria-presence"
          v-model="presence"
          name="textContentCriteria_presence"
          class="form-select col-4"
          :disabled="props.initialValue"
        >
          <option
            v-for="opt in filterOptions"
            :key="opt"
            :value="opt"
          >
            {{ filterData.subFilters[0].labels[opt] }}
          </option>
        </select>
        <select
          v-if="specifyDocumentCategory"
          id="text-content-criteria-document-category"
          v-model="documentCategory"
          name="textContentCriteria_documentCategory"
          class="form-select"
        >
          <option
            v-for="(docCategory, idx) in textualReportCategories"
            :key="idx"
            :value="idx"
          >
            {{ docCategory }}
          </option>
        </select>
        <input
          id="text-content-criteria-input"
          v-model="value"
          name="textContentCriteria_input"
          class="form-control col p-1"
          @keyup.enter="addFilter"
        >
        <input
          v-if="specifyWordDistance"
          id="text-content-criteria-word-distance"
          v-model="wordDistance"
          class="form-control col-1 text-center"
        >
        <div class="input-group-append">
          <button
            id="text-content-criteria-add"
            name="textContentCriteria_ok"
            class="col-auto btn btn-primary"
            type="button"
            @click="addFilter"
          >
            {{ t('ok') }}
          </button>
        </div>
      </div>
    </div>
    <div
      v-else
      class="col-12"
    >
      <div class="row">
        <select
          id="text-content-criteria-presence"
          v-model="presence"
          name="textContentCriteria_presence"
          class="form-select col-12 mb-2"
          :disabled="props.initialValue"
        >
          <option
            v-for="opt in filterOptions"
            :key="opt"
            :value="opt"
          >
            {{ filterData.subFilters[0].labels[opt] }}
          </option>
        </select>
        <select
          v-if="specifyDocumentCategory"
          id="text-content-criteria-document-category"
          v-model="documentCategory"
          name="textContentCriteria_documentCategory"
          class="form-select col-12 mb-2"
        >
          <option
            v-for="(docCategory, idx) in textualReportCategories"
            :key="idx"
            :value="idx"
          >
            {{ docCategory }}
          </option>
        </select>
        <div class="input-group mb-2">
          <input
            id="text-content-criteria-input"
            v-model="value"
            name="textContentCriteria_input"
            class="form-control col p-1"
            @keyup.enter="addFilter"
          >
          <input
            v-if="specifyWordDistance"
            id="text-content-criteria-word-distance"
            v-model="wordDistance"
            class="form-control col-2 text-center"
          >
          <div class="input-group-append">
            <button
              id="text-content-criteria-add"
              name="textContentCriteria_ok"
              class="col-auto btn btn-primary"
              type="button"
              :disabled="!canSubmit"
              @click="addFilter"
            >
              {{ t('ok') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      :class="isLegacy ? ['col-8', 'ps-5'] : []"
    >
      <div
        v-if="isLegacy && extendedSearch && presence === 'absent'"
        class="form-check"
      >
        <input
          id="hard-absent-content-checkbox"
          v-model="hardAbsentContent"
          type="checkbox"
          class="form-check-input"
        >
        <label
          for="hard-absent-content-checkbox"
          class="form-check-label"
        >
          {{ t('filters.textCondition.hardAbsentContent') }}
        </label>
      </div>
      <div v-if="extendedSearch && (presence === 'present' || hardAbsentContent)">
        <div class="form-check">
          <input
            id="include-antecedent-checkbox"
            v-model="includeAntecedents"
            type="checkbox"
            class="form-check-input"
          >
          <label
            for="include-antecedent-checkbox"
            class="form-check-label"
          >
            {{ t('filters.textCondition.includeAntecedents') }}
          </label>
        </div>
        <div class="form-check">
          <input
            id="include-family-antecedent-checkbox"
            v-model="includeFamilyAntecedents"
            type="checkbox"
            class="form-check-input"
          >
          <label
            for="include-family-antecedent-checkbox"
            class="form-check-label"
          >
            {{ t('filters.textCondition.includeFamilyAntecedents') }}
          </label>
        </div>
        <div class="form-check">
          <input
            id="include-negation-checkbox"
            v-model="includeNegations"
            type="checkbox"
            class="form-check-input"
          >
          <label
            for="include-negation-checkbox"
            class="form-check-label"
          >
            {{ t('filters.textCondition.includeNegations') }}
          </label>
        </div>
        <div class="form-check">
          <input
            id="include-word-distance-checkbox"
            v-model="specifyWordDistance"
            type="checkbox"
            class="form-check-input"
          >
          <label
            for="include-word-distance-checkbox"
            class="form-check-label"
          >
            {{ t('filters.textCondition.enableIntervals') }}
          </label>
        </div>
        <div
          v-if="isSearchManager"
          class="form-check"
        >
          <input
            id="document-category-checkbox"
            v-model="specifyDocumentCategory"
            type="checkbox"
            class="form-check-input"
          >
          <label
            for="document-category-checkbox"
            class="form-check-label"
          >
            {{ t('filters.textCondition.enableDocumentCategory') }}
          </label>
        </div>
      </div>
      <button
        v-if="extendedSearch === false && (isLegacy || presence === 'present' || hardAbsentContent)"
        id="text-content-criteria-more-options"
        class="btn btn-link"
        @click="extendedSearch = true"
      >
        <small>{{ t('filters.moreOptions') }}</small>
      </button>
    </div>
  </div>
</template>
